import {
  assignableRoles,
  INVITATIONS_QUERY_KEY,
  ROLE_PINNED_MESSAGE,
  roleLabel,
  type CreateInvitationResponse,
  type OrgRole,
} from '@/components/team/team-types';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FormFieldGroup,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

export interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether the active org is the internal Destination Tours org. */
  isInternalOrg: boolean;
}

const DEFAULT_ROLE: OrgRole = 'org:member';

/**
 * Invite a member to the active organization.
 *
 * Role selection is advisory in one case only: when the active org is the
 * Destination Tours (internal) organization, the server pins external
 * (non @destinationtours.com) invitees to Hunts Admin — hunts access only —
 * regardless of the choice here. Members of any OTHER organization get the
 * selected role as-is, with that org's normal Tours + Hunts access.
 */
export function InviteMemberDialog({ open, onOpenChange, isInternalOrg }: InviteMemberDialogProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>(DEFAULT_ROLE);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setEmail('');
      setRole(DEFAULT_ROLE);
      setError(undefined);
    }
  }, [open]);

  const inviteMutation = useMutation({
    mutationFn: async () =>
      apiClient.post<CreateInvitationResponse>('/organizations/invitations', { email, role }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: INVITATIONS_QUERY_KEY });
      if (response.data?.rolePinned) {
        toast.success(`Invitation sent as Hunts Admin. ${ROLE_PINNED_MESSAGE}.`);
      } else {
        toast.success('Invitation sent');
      }
      onOpenChange(false);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to send invitation');
    },
  });

  const handleSubmit = () => {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setError(undefined);
    inviteMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite member</DialogTitle>
          <DialogDescription>
            They&apos;ll receive an email invitation to join your organization on Destination
            Hunts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormFieldGroup name="email" label="Email address" required error={error}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="name@company.com"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            />
          </FormFieldGroup>

          <div className="space-y-2">
            <Label htmlFor="invite-role">Role</Label>
            <Select value={role} onValueChange={(value: string) => setRole(value as OrgRole)}>
              <SelectTrigger id="invite-role" className="w-full">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {assignableRoles(isInternalOrg).map((option) => (
                  <SelectItem key={option} value={option}>
                    {roleLabel(option)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isInternalOrg && (
              <p className="text-xs text-muted-foreground">
                People outside @destinationtours.com join the Destination Tours organization as
                Hunts Admin (hunts access only).
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={inviteMutation.isPending}>
            Send invitation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
